import React, { useState, useEffect, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import http from "@shared/api/http";
import Loader from "@shared/components/Loader";
import "./Programs.css";

const DAYS_OF_WEEK = [
  'monday',
  'tuesday', 
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

const PatientCalendar = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [calendarData, setCalendarData] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // removed unused currentPatientId state

  // request headers are created inside the data loader to avoid hook deps churn

  const loadCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const url = "/:locale/react/:token/calendar/patient_schedule";
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
      };
      const response = await http.get(url, { headers });
      
      if (response.data.success) {
        setCalendarData(response.data.data.assignments);
      }
    } catch (error) {
      console.error("Error loading calendar data:", error);
      setError("Fehler beim Laden der Kalenderdaten");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load calendar data directly (backend will determine patient from token)
  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // removed unused handleMarkCompleted

  const getDayDisplayName = (day) => {
    const dayNames = {
      monday: t("calendar.days.monday"),
      tuesday: t("calendar.days.tuesday"),
      wednesday: t("calendar.days.wednesday"),
      thursday: t("calendar.days.thursday"),
      friday: t("calendar.days.friday"),
      saturday: t("calendar.days.saturday"),
      sunday: t("calendar.days.sunday")
    };
    return dayNames[day] || day;
  };

  const renderDayCard = (day) => {
    const dayAssignments = calendarData[day] || [];
    
    return (
      <Card key={day} className="calendar-day-card h-100 patient-calendar-card">
        <Card.Header className="calendar-day-header">
          <h6 className="mb-0">{getDayDisplayName(day)}</h6>
        </Card.Header>
        <Card.Body className="calendar-day-body">
          {dayAssignments.length === 0 ? (
            <div className="calendar-empty-day">
              <p className="text-muted small">{t("calendar.noPrograms")}</p>
            </div>
          ) : (
            <>
              {dayAssignments.map((assignment) => (
                <div key={assignment.id} className="calendar-program-item mb-2 patient-program-item">
                  <div className="program-info-patient">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <strong>
                          <Link to={`/programs/${assignment.program_id}`} className="program-link">
                            {assignment.program_name}
                          </Link>
                        </strong>
                        <br />
                        <small className="text-muted">
                          {t("program.duration")} {t("options.duration." + assignment.duration_minutes)}
                        </small>
                        {assignment.notes && (
                          <>
                            <br />
                            <small className="text-muted">
                              {t("calendar.notes")}: {assignment.notes}
                            </small>
                          </>
                        )}
                      </div>
                    </div>
                    {assignment.is_completed && (
                      <div className="completion-status">
                        <small className="text-success">
                          ✓ {t("calendar.completed")}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderDesktopCalendar = () => (
    <Row className="g-2">
      {DAYS_OF_WEEK.map((day) => (
        <Col key={day} className="mb-3 px-1">
          {renderDayCard(day)}
        </Col>
      ))}
    </Row>
  );

  const renderMobileCalendar = () => (
    <>
      {DAYS_OF_WEEK.map((day) => (
        <Row key={day} className="mb-3 g-0">
          <Col xs={12} className="px-2">
            {renderDayCard(day)}
          </Col>
        </Row>
      ))}
    </>
  );

  if (loading) {
    return (
      <Container fluid className="calendar-view-container px-0">
        <div className="text-center py-5">
          <Loader />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="calendar-view-container px-0">
        <div className="text-center py-5">
          <p className="text-danger">{error}</p>
          <Button variant="primary" onClick={loadCalendarData}>
            {t("calendar.retry")}
          </Button>
        </div>
      </Container>
    );
  }

  const hasAnyAssignments = DAYS_OF_WEEK.some(day => calendarData[day]?.length > 0);

  return (
    <Container fluid className="calendar-view-container px-0 patient-calendar">
      <Row className="mb-3">
        <Col>
          <div className="calendar-intro">
            <h5>{t("calendar.weeklySchedule")}</h5>
            <p className="text-muted">{t("calendar.patientInstructions")}</p>
          </div>
        </Col>
      </Row>
      
      {!hasAnyAssignments ? (
        <div className="text-center py-5">
          <p className="text-muted">{t("calendar.noScheduleAssigned")}</p>
          <p className="text-muted">{t("calendar.contactTherapist")}</p>
        </div>
      ) : (
        <div className="calendar-content-wrapper">
          {isMobile ? renderMobileCalendar() : renderDesktopCalendar()}
        </div>
      )}
    </Container>
  );
};

export default PatientCalendar; 